import { Injectable } from '@angular/core';
import { LoggerService } from 'src/app/core/services/logger.service';
import { environment } from 'src/environments/environment';
import { JsonConverterUtils } from '../CXOne/util/JsonConverterUtils';

export type Ticket = { id_interacao: string | null; id_ticket: string | null };

const templateDesligou: string = 'cb_nice_desligou';
@Injectable({
  providedIn: 'root',
})
export class DynamicsCorretora {
  service: string = 'DYNAMICS Corretora';
  private readonly RECALL_LIMIT = 2;
  private readonly RECALL_DELAY_MS = 2000;

  constructor(
    private logger: LoggerService,
    private jsonConverterUtils: JsonConverterUtils,
  ) {}

  initialize(cifUrl: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.logger.info(`${this.service} - inicializando CIF: ${cifUrl}`);
      const script = this.createScriptElement(cifUrl);
      document.body.appendChild(script);
      const onCIFInitDone = async (event: Event) => {
        try {
          await this.configureCIF();
          this.logger.info(`${this.service} - CIF inicializado com sucesso`);
          resolve();
        } catch (error) {
          if (error instanceof Error && error.message) {
            reject(error);
          } else {
            this.logger.error(
              `${this.service} - Erro ao criar sessão: `,
              error,
            );
            reject(
              new Error(
                `${this.service} - Erro ao criar sessão: ${error instanceof Error ? error.message : error}`,
              ),
            );
          }
        } finally {
          window.removeEventListener('CIFInitDone', onCIFInitDone);
        }
      };
      window.addEventListener('CIFInitDone', onCIFInitDone);
    });
  }

  public async cifCreateSession(input_template: any): Promise<string> {
    try {
      const success = await window.Microsoft.CIFramework.canCreateSession();
      if (success === true) {
        const sessionId = await (
          window as any
        ).Microsoft.CIFramework.createSession(input_template);
        this.logger.info('DYNAMICS - sessionId do createSession', sessionId);
        return sessionId;
      } else {
        this.logger.warn(
          'DYNAMICS - Não é possível criar uma sessão com o template fornecido.',
        );
        return '';
      }
    } catch (error: any) {
      this.logger.info('DYNAMICS - erro no callback do createSession', error);
      throw error;
    }
  }

  public async cifCloseSession(sessionId: string): Promise<void> {
    try {
      await (window as any).Microsoft.CIFramework.raiseEvent('ControleQueda', {
        sessionId: sessionId,
      });

      var input = {
        templateName: templateDesligou,
        templateParameters: {},
      };
      await (window as any).Microsoft.CIFramework.notifyEvent(input);

      await (window as any).Microsoft.CIFramework.raiseEvent('ControleSessao', {
        sessionId: sessionId,
      });

      this.logger.info(`DYNAMICS - Sessão encerrada com sucesso: ${sessionId}`);
    } catch (error: any) {
      this.logger.error(`${this.service} - Erro ao encerrar sessão: `, error);
      throw error;
    }
  }

  private createScriptElement(cifUrl: string): HTMLScriptElement {
    const script = document.createElement('script');
    script.src = cifUrl;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-crmurl', cifUrl);
    script.setAttribute('data-cifid', 'CIFMainLibrary');
    return script;
  }

  private async configureCIF(): Promise<void> {
    try {
      await (window as any).Microsoft.CIFramework.setMode(1);
      await (window as any).Microsoft.CIFramework.addHandler(
        'onmodechanged',
        async (eventData: any) => {
          await (window as any).Microsoft.CIFramework.setMode(1);
          return Promise.resolve();
        },
      );
      /*await (window as any).Microsoft.CIFramework.setClickToAct(true);
            await (window as any).Microsoft.CIFramework.addHandler(
              'onSessionClosed',
              this.cifCloseSession(),
            );*/
      await (window as any).Microsoft.CIFramework.setWidth(400);
      this.logger.info('Cif Injetado com sucesso');
    } catch (error) {
      this.logger.error(`${this.service} - Erro ao configurar CIF: `, error);
      throw new Error(`${this.service} - Erro ao configurar CIF: ${error}`);
    }
  }

  public async cifCreateIncident(
    tableName: string,
    attachData: any,
  ): Promise<Ticket> {
    let retorno: Ticket = {
      id_interacao: null,
      id_ticket: null,
    };

    try {
      const result = await (window as any).Microsoft.CIFramework.createRecord(
        tableName,
        JSON.stringify(attachData),
      );
      this.logger.info(`DYNAMICS - ID Interação criado: ${result}`);
      retorno.id_interacao = this.jsonConverterUtils.JsonParsed(result).id;

      //ID para teste
      //retorno.id_interacao = "a3a9ce5f-d773-f011-bec2-6045bd3a8bc6"

      //recuperacao do ID do ticket
      if (retorno.id_ticket == null && retorno.id_interacao != null) {
        retorno.id_ticket = await this.cifRetrieveRecord(
          retorno.id_interacao,
          tableName,
        );
        this.logger.info(`DYNAMICS - Ticket recuperado: ${retorno.id_ticket}`);
      }
    } catch (error) {
      throw new Error(
        `Erro ao criar interação ou recuperar ticket: ${error instanceof Error ? error.message : error}`,
      );
    }
    return retorno;
  }

  private async cifRetrieveRecord(
    interactionId: string,
    tableName: string,
  ): Promise<string | null> {
    let idTicket = null;

    for (let attempt = 0; attempt < this.RECALL_LIMIT; attempt++) {
      await this.delay(this.RECALL_DELAY_MS);

      const options = `?$select=incidentid, ticketnumber`;
      try {
        const result = await (
          window as any
        ).Microsoft.CIFramework.retrieveRecord(
          tableName,
          interactionId,
          options,
        );
        const interaction = this.jsonConverterUtils.JsonParsed(result);

        this.logger.info('interaction retrieved: ', interaction);

        if (
          interaction.ticketnumber != '' ||
          interaction.ticketnumber != null
        ) {
          idTicket = interaction.ticketnumber;
          this.logger.info(
            `DYNAMICS - Ticket ID encontrado na tentativa ${attempt + 1}: ${idTicket}`,
          );
          break;
        }
      } catch (error) {
        throw new Error(
          `Erro ao recuperar ticket na tentativa ${attempt + 1}: ${error instanceof Error ? error.message : error}`,
        );
      }
    }

    if (!idTicket) {
      this.logger.warn(
        `DYNAMICS - Limite de tentativas (${this.RECALL_LIMIT}) atingido ao recuperar o Ticket ID para a interação ${interactionId}`,
      );
    }

    return idTicket;
  }

  public async cifSearchAndOpenRecords(
    tableName: string,
    phoneNumber: string,
  ): Promise<string | null> {
    var customerId = '';

    const options = `?$filter=mobilephone eq '${phoneNumber}'&$select=fullname,telephone1,contactid`;

    try {
      const result = await window.Microsoft.CIFramework.searchAndOpenRecords(
        tableName,
        options,
        true,
      );

      const interaction = this.jsonConverterUtils.JsonParsed(result);

      this.logger.info('interaction retrived: ', interaction);

      if (Object.keys(interaction).length > 0) {
        customerId = interaction[0].contactid;
      } else {
        customerId = environment.esteiras_corretora.contato_default; //Id 'Usuário Desconhecido'
      }
    } catch (error) {
      throw new Error(
        `Erro ao buscar o telefone do Cliente: ${error instanceof Error ? error.message : error}`,
      );
    }

    return customerId;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
